import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Form} from '@ui/forms/form';
import {useDataTable} from './data-table-context';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {useDownloadSelectedRows} from '@common/datatable/requests/download-selected-rows';
import {useUser} from '@common/auth/ui/use-user';
import {debridAlldebrid} from '@common/auth/alldebrid';
import {SectionHelper} from '@common/ui/other/section-helper';
import {ErrorIcon} from '@ui/icons/material/Error';
import useClipboard from 'react-use-clipboard';

import {apiClient} from '@common/http/query-client';
import {ResponseDownload} from '@app/liens/use-download-link';
import {Lien} from '@app/titles/models/lien';
import {Skeleton} from '@ui/skeleton/skeleton';
import {useAuth} from '@common/auth/use-auth';
import TurnstileWidget from '@app/liens/turnstile-widget';

const cache: any = {};

async function fetchDownload(id: number) {
  return await apiClient
    .get<ResponseDownload>(`liens/${id}/download`)
    .then(response => response.data);
}

export function DownloadSelectedItemsAction() {
  const {selectedRows, setSelectedRows} = useDataTable();
  const [download, setDownload] = useState(false);
  const {data, isLoading} = useDownloadSelectedRows(selectedRows, download);
  const handleDownloadClick = () => {
    setDownload(true);
  };
  useEffect(() => {
    if (data) {
      setDownload(false);
    }
  }, [data]);
  return (
    <DialogTrigger type="modal">
      <Button
        variant="flat"
        onClick={handleDownloadClick}
        color="primary"
        className="ml-auto"
      >
        <Trans message="Download" />
      </Button>
      {isLoading || !data?.liens ? (
        <Skeleton />
      ) : (
        <DownloadItemsDialog downloadSelectedRows={data.liens} />
      )}
    </DialogTrigger>
  );
}

export function DownloadPartsItemsAction({id}: {id: Lien['id']}) {
  const [token, setToken] = useState('');
  const {user} = useAuth();
  const handleCaptchaVerify = (token: string) => {
    setToken(token);
  };
  const {data, isLoading} = useDownloadSelectedRows(
    [id],
    !!(user?.IsPremium || token?.length),
    token,
  );

  return (isLoading || !data?.liens) && user?.IsPremium ? (
    <Skeleton />
  ) : (user?.IsPremium || token?.length) && data?.liens ? (
    <DownloadItemsDialog downloadSelectedRows={data.liens} />
  ) : (
    <Dialog size="w-850">
      <DialogHeader>
        <Trans message="Please verify that you are not a robot" />
      </DialogHeader>

      <DialogBody>
        <TurnstileWidget onVerify={handleCaptchaVerify} />
      </DialogBody>
    </Dialog>
  );
}

function DownloadItemsDialog({
  downloadSelectedRows,
}: {
  downloadSelectedRows: Lien[];
}) {
  const {user} = useAuth();

  const {formId} = useDialogContext();
  const form = useForm();
  const [errors, setErrors] = useState<string[]>([]);

  const [isCopied, copyToClipboard] = useClipboard(
    form.getValues('links') || '',
    {
      successDuration: 1000,
    },
  );

  function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  useEffect(() => {
    async function debridLinks() {
      form.setValue('links', '');
      setErrors([]);
      if (downloadSelectedRows.length) {
        const alldebrid =
          user?.debridors.length &&
          user?.debridors.find(debridor => debridor.provider === 2);

        for (const link of downloadSelectedRows) {
          if (alldebrid && link.id_host !== 2) {
            if (cache[link.lien]) {
              form.setValue(
                'links',
                form.getValues('links') + cache[link.lien] + '\n',
              );
            } else {
              const debridResult = await debridAlldebrid(
                alldebrid.api,
                link.lien,
              );
              if (debridResult.success && debridResult.link) {
                cache[link.lien] = debridResult.link;
                form.setValue(
                  'links',
                  form.getValues('links') + debridResult.link + '\n',
                );
              } else {
                cache[link.lien] = link.lien;
                form.setValue(
                  'links',
                  form.getValues('links') + link.lien + '\n',
                );
                if (debridResult.error) {
                  setErrors(prevErrors => [
                    ...prevErrors,
                    link.lien + ' : ' + debridResult.error,
                  ]);
                }
              }
              await delay(700); // Attente de 700ms entre chaque appel
            }
          } else {
            const data = await fetchDownload(link.id);
            if (data.debrided && data.directDL) {
              form.setValue(
                'links',
                form.getValues('links') + data.directDL + '\n',
              );
            } else {
              if (data.error) {
                setErrors(prevErrors => [
                  ...prevErrors,
                  link.lien + ' : ' + data.error,
                ]);
              }
              form.setValue(
                'links',
                form.getValues('links') + link.lien + '\n',
              );
            }
          }
        }
      }
    }

    if (user?.IsPremium && user?.more_info.debrid_link) {
      debridLinks();
    } else {
      if (downloadSelectedRows.length) {
        form.setValue('links', '');
        for (const link of downloadSelectedRows) {
          form.setValue('links', form.getValues('links') + link.lien + '\n');
        }
      }
    }
  }, [downloadSelectedRows]);

  return (
    <Dialog size="w-850">
      <DialogHeader>
        <Trans
          message="Download [one 1 item|other :count items]"
          values={{count: downloadSelectedRows.length}}
        />
      </DialogHeader>

      <DialogBody>
        {errors.length > 0 && <ErrorStatus errors={errors} />}
        <Form className={'mt-20'} form={form} onSubmit={() => {}} id={formId}>
          <FormTextField
            name={'links'}
            form={formId}
            label={<Trans message="Links" />}
            inputElementType="textarea"
            rows={10}
            className="mb-24"
          />
        </Form>
        <Button variant="flat" color="primary" onClick={copyToClipboard}>
          {isCopied ? <Trans message="Copied!" /> : <Trans message="Copy" />}
        </Button>
      </DialogBody>
    </Dialog>
  );
}

function ErrorStatus({errors}: {errors: string[]}) {
  return (
    <SectionHelper
      size={'sm'}
      leadingIcon={<ErrorIcon size="xs" className="text-danger" />}
      description={errors.map((error, index) => (
        <p key={index}>{error}</p>
      ))}
      title={'Erreur de Débridage'}
      color="neutral"
    />
  );
}
